import Accordion from "@/common/components/Accordion";
import Checkbox from "@/common/components/Checkbox";
import MaskedTextField from "@/common/components/MaskedTextField";
import TextField from "@/common/components/TextField";
import { cnpjCpfMask, stateInscriptionMask } from "@/common/constants/masks.constant";
import { AddressData } from "@/common/types/address";
import AddressForm from "@/common/components/AddressForm";
import { Control, FieldErrors, UseFormClearErrors, UseFormGetValues, UseFormRegister, UseFormReset, UseFormSetError, UseFormSetValue, UseFormWatch, useWatch } from "react-hook-form";
import { AddressValidationSchema } from "@/common/schemas/address.schema";
import { CompanyBranchValidationSchema } from "../../schemas/companyBranch.schema";
import { CompanyBranchData, CompanyData } from "@/features/company/types/company.type";
import { ImageUploader } from "@/common/components/ImageUploader";
import { checkIsImage } from "@/common/utils/fileValidator";
import { GeneralInformationData } from "@/common/types/generalInformation.type";
import { useEffect } from "react";
import { strategy as kindStrategy } from "../../../../common/constants/kind.constant";
import { useCompanyBranch } from "@/common/hooks/queries/useCompanyBranch";
import { formatCnpjCpf } from "@/common/utils/formatters";


interface GeneralInformationProps {
  companyData?: CompanyData;
  branchData?: CompanyBranchData;
  register: UseFormRegister<CompanyBranchValidationSchema>;
  errors: FieldErrors<CompanyBranchValidationSchema>;
  control: Control<CompanyBranchValidationSchema, any>;
  watch: UseFormWatch<CompanyBranchValidationSchema>;
  setValue: UseFormSetValue<CompanyBranchValidationSchema>;
  setError: UseFormSetError<CompanyBranchValidationSchema>;
  clearErrors: UseFormClearErrors<CompanyBranchValidationSchema>
  file: File | undefined;
  setFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  companyLogo?: GeneralInformationData['company_logo'];
  disableCnpj?: boolean;
  disableFields?: boolean;
  reset?: UseFormReset<CompanyBranchValidationSchema>
  getValues?: UseFormGetValues<CompanyBranchValidationSchema>
}

export const GeneralInformation = ({
  companyData,
  register,
  errors,
  clearErrors,
  watch,
  setValue,
  setError,
  file,
  setFile,
  companyLogo,
  disableCnpj,
  control,
  branchData,
  disableFields,
  reset,
  getValues
}: GeneralInformationProps) => {
  const generalInformationAttributes = watch('general_information_attributes');

  const selectedParent = useWatch({
    control,
    name: "parent.value",
  });

  const willUseOwnData = useWatch({
    control,
    name: "general_information_attributes.willUseOwnData",
  });

  const { data: parentData, isPending: parentDataIsPending } = useCompanyBranch(selectedParent || '', !!selectedParent, false);

  const { data: matrixBranchData } = useCompanyBranch('matrix_general_information', !parentDataIsPending && !parentData?.general_information);

  const branchFormData = willUseOwnData ? branchData?.general_information : parentData?.general_information || matrixBranchData?.general_information || companyData?.general_information

  useEffect(() => {
    if (willUseOwnData && !branchFormData && reset && getValues) {
      setValue('general_information_attributes.willUseOwnData', true)
      setValue('general_information_attributes.cnpj', null)
      setValue('general_information_attributes.email', null)
      setValue('general_information_attributes.fantasy_name', null)
      setValue('general_information_attributes.official_name', null)
      setValue('general_information_attributes.primary_cnae', null)
      setValue('general_information_attributes.state_inscription', null)
      setValue('general_information_attributes.address_attributes', undefined)
      setValue('general_information_attributes.address', undefined)
    } else {
      let newFormParams = { ...generalInformationAttributes, ...branchFormData, address_attributes: branchFormData?.address }
      newFormParams.cnpj = formatCnpjCpf(newFormParams.cnpj + '')
      setValue('general_information_attributes', newFormParams)
    }
  }, [branchFormData])

  return (
    <Accordion
      contentClassName="data-[state=open]:overflow-visible"
      head={(
        <div className="flex flex-col gap-2 ">
          {errors.general_information_attributes?.willUseOwnData && (
            <p className="text-sm text-danger-soft">
              {errors.general_information_attributes.willUseOwnData.message}
            </p>
          )}
          <Checkbox
            label='Dados empresariais específicos'
            onClick={(event) => {
              event.stopPropagation()
            }}
            {...register('general_information_attributes.willUseOwnData')}
            disabled={disableFields}
          />
        </div>
      )}
      body={(
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 items-center">
            <div>
              <ImageUploader
                isLoading={false}
                label="Logo"
                name="profile-picture"
                accept="image/jpg, image/jpeg, image/png"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement & { files: FileList; }).files?.[0];
                  if (!file || !checkIsImage(file)) {
                    return;
                  }
                  setFile(file);
                }}
                currentImageUrl={file
                  ? URL.createObjectURL(file)
                  : companyLogo?.url
                    ? companyLogo?.url
                    : undefined
                }
                disabled={!willUseOwnData || disableFields}
              />
            </div>
            <div className="flex gap-2 w-full">
              <MaskedTextField
                mask={cnpjCpfMask}
                className="w-1/2"
                label="CNPJ/CPF"
                markAsRequired={!disableFields}
                disabled={!willUseOwnData || disableCnpj || disableFields}
                placeholder={!willUseOwnData ? companyData?.matrix_branch?.general_information?.cnpj : ''}
                errorMessage={errors?.general_information_attributes?.cnpj?.message || undefined}
                {...register('general_information_attributes.cnpj')}
              />
              <TextField
                label="Tipo de empresa"
                className="w-1/2"
                value={companyData?.kind ? kindStrategy[companyData.kind as keyof typeof kindStrategy] : ''}
                disabled
              />
            </div>
          </div>
          <div className="flex gap-2">
            <TextField
              label="Nome Fantasia"
              markAsRequired={!disableFields}
              className="w-1/2"
              maxLength={64}
              disabled={!willUseOwnData || disableFields}
              placeholder={!willUseOwnData ? branchFormData?.fantasy_name : ''}
              errorMessage={errors?.general_information_attributes?.fantasy_name?.message || undefined}
              {...register('general_information_attributes.fantasy_name')}
            />
            <TextField
              label="Nome Empresarial"
              markAsRequired={!disableFields}
              className="w-1/2"
              maxLength={64}
              disabled={!willUseOwnData || disableFields}
              placeholder={!willUseOwnData ? branchFormData?.official_name : ''}
              errorMessage={errors?.general_information_attributes?.official_name?.message || undefined}
              {...register('general_information_attributes.official_name')}
            />
          </div>
          <TextField
            label="Endereço eletrônico"
            markAsRequired={!disableFields}
            maxLength={64}
            errorMessage={errors?.general_information_attributes?.email?.message || undefined}
            disabled={!willUseOwnData || disableFields}
            placeholder={!willUseOwnData ? branchFormData?.email : ''}
            {...register('general_information_attributes.email')}
          />
          <div className="flex gap-2">
            <MaskedTextField
              label="IE (Inscrição Estadual)"
              markAsRequired={!disableFields}
              mask={stateInscriptionMask}
              errorMessage={errors?.general_information_attributes?.state_inscription?.message || undefined}
              className="w-1/2"
              disabled={!willUseOwnData || disableFields}
              value={generalInformationAttributes?.state_inscription || branchFormData?.state_inscription || ''}
              placeholder={!willUseOwnData ? branchFormData?.state_inscription : ''}
              {...register('general_information_attributes.state_inscription')}
            />
            <TextField
              label="CNAE"
              markAsRequired={!disableFields}
              maxLength={7}
              errorMessage={errors?.general_information_attributes?.primary_cnae?.message || undefined}
              className="w-1/2"
              disabled={!willUseOwnData || disableFields}
              placeholder={!willUseOwnData ? branchFormData?.primary_cnae : ''}
              {...register('general_information_attributes.primary_cnae')}
            />
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex justify-between">
              <p className="text-[18px] font-bold">
                Endereço
              </p>
            </div>
            <AddressForm
              initialAddress={!willUseOwnData ? branchFormData?.address as AddressData : generalInformationAttributes?.address_attributes as AddressData || {}}
              onSetAddress={(address: AddressData, valid: boolean) => {
                setValue("general_information_attributes.address_attributes", address);
                if (!valid) {
                  setError("general_information_attributes.address_attributes.zipcode", { message: "CEP não encontrado" });
                } else {
                  clearErrors("general_information_attributes.address_attributes.zipcode");
                }
              }}
              disableFields={!willUseOwnData || disableFields}
              errors={errors?.general_information_attributes?.address_attributes as FieldErrors<AddressValidationSchema>}
              markFieldsAsRequired={!disableFields}
            />
          </div>
        </div>
      )}
    />
  );
};
